import { FullCourse } from 'server/types';
import { AsyncForm, FormGroup, FormSubSection, InputLabel, Subtext } from '@components/async-form';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary } from '@components/buttons';
import { showToast } from '@components/toaster';
import { COURSE_STUDENT_LIMIT } from 'shared/consts';
import { useCurrentTenant } from '@components/router/session-context';
import { HeadingSecondary } from '@course/components/headings';
import { useMinidoc } from '@components/minidoc';
import { ManualDom } from '@components/manual-dom';
import { EditorWrapper } from '@components/minidoc/minidoc-root';
import { cardMiddleware, defaultToolbarActions, minidocToolbar, placeholder } from 'minidoc-editor';
import { toolbarDropdown } from '@components/toolbar-dropdown';
import { useIntl } from 'shared/intl/use-intl';
import { getCourseNoSeatsAvailableMessage } from 'shared/messages';
import { mediaCard, mediaMiddleware } from '@components/media-card';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { Toggle } from '@components/toggle';
import { courseLabel } from 'shared/terminology';

const store = rpx.courseSettings;

interface Props {
  course: FullCourse;
  onUpdate: (updates: Pick<FullCourse, 'maxMemberships' | 'isMembershipLimited'>) => void;
}

function NoSeatsAvailableMessageEditor({
  message,
  onChange,
}: {
  message: string;
  onChange: (doc: string) => void;
}) {
  const intl = useIntl();
  const editor = useMinidoc({
    doc: message,
    middleware: () => [
      placeholder('No seats available message'),
      minidocToolbar([
        ...defaultToolbarActions.filter((a) => a.id !== 'h1'),
        toolbarDropdown({
          mediaOnly: true,
          intl,
        }),
      ]),
      cardMiddleware([mediaCard]),
      mediaMiddleware({
        isPublic: true,
      }),
    ],
    onChange: (doc) => {
      onChange(doc);
    },
  });

  return (
    <div class="dark:bg-gray-800 border dark:border-gray-700 rounded-sm">
      <ManualDom el={editor.toolbar.root} />
      <div class="bg-white dark:bg-gray-900 p-4 rounded-b-lg">
        <EditorWrapper class="prose" editor={editor} />
      </div>
    </div>
  );
}

export function LimitSettings({ course, onUpdate }: Props) {
  const intl = useIntl();
  const tenant = useCurrentTenant();
  const [settings, setSettings] = useAsyncState(
    () => store.getCourseMembershipLimits({ courseId: course.id }),
    [course.id],
  );

  const terminologyCourse = courseLabel({
    course,
    tenant,
  });

  async function submit(formData: { maxMemberships: number; isMembershipLimited: boolean }) {
    const newLimit = formData.maxMemberships
      ? Number(formData.maxMemberships)
      : COURSE_STUDENT_LIMIT;
    const isDefaultMessage =
      settings?.noSeatsAvailableMessage ===
      getCourseNoSeatsAvailableMessage(intl, course.guide.email);

    const isMembershipLimited = formData.isMembershipLimited
      ? Boolean(formData.isMembershipLimited)
      : false;

    await store.setCourseMembershipLimits({
      courseId: course.id,
      maxMemberships: newLimit,
      isMembershipLimited: isMembershipLimited,
      noSeatsAvailableMessage: isDefaultMessage ? undefined : settings?.noSeatsAvailableMessage,
    });
    showToast({
      title: 'Settings changed',
      message: `${terminologyCourse} limit settings have been updated.`,
      type: 'ok',
    });
    onUpdate({
      maxMemberships: newLimit,
      isMembershipLimited: isMembershipLimited,
    });
  }

  if (!settings) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  function toggleMembershipLimit() {
    if (!settings) {
      return;
    }

    setSettings({
      ...settings,
      isMembershipLimited: !settings.isMembershipLimited,
    });
  }

  return (
    <AsyncForm class="flex flex-col gap-6" onSubmit={submit}>
      <HeadingSecondary>{terminologyCourse} Limits</HeadingSecondary>
      <FormSubSection>
        <InputLabel>Limit the number of seats.</InputLabel>
        <FormGroup prop="isMembershipLimited">
          <Toggle
            checked={settings.isMembershipLimited}
            name="isMembershipLimited"
            onClick={toggleMembershipLimit}
          />
        </FormGroup>
      </FormSubSection>

      {settings.isMembershipLimited && (
        <FormSubSection>
          <InputLabel>Seats available for this {terminologyCourse}</InputLabel>
          <Subtext>Invitations do not count towards this limit.</Subtext>
          <FormGroup prop="maxMemberships">
            <input
              class="inline-ruz-input w-full"
              type="text"
              name="maxMemberships"
              placeholder="Unlimited"
              min="0"
              max={COURSE_STUDENT_LIMIT}
              defaultValue={`${settings.maxMemberships || COURSE_STUDENT_LIMIT}`}
            />
          </FormGroup>
        </FormSubSection>
      )}

      <FormSubSection>
        <InputLabel>No seats available message</InputLabel>
        <Subtext>
          This message is displayed to students when the {terminologyCourse} is full
        </Subtext>
        <NoSeatsAvailableMessageEditor
          message={
            settings.noSeatsAvailableMessage ||
            getCourseNoSeatsAvailableMessage(intl, course.guide.email)
          }
          onChange={(message) => setSettings({ ...settings, noSeatsAvailableMessage: message })}
        />
      </FormSubSection>
      <footer>
        <BtnPrimary>Save</BtnPrimary>
      </footer>
    </AsyncForm>
  );
}
